'use client'

import * as React from 'react'
import { useParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useIsLoggedInQuery } from 'auth/hooks'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAdd } from '@repo/design-system-cf/components/icons'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import {
  GetImagesQueryRootKey,
  ListQueryRootKey,
  getImagesFn,
  useListInfiniteQuery,
} from '@web/gen/service/creativefabrica/vectorizer/v2'
import { ToolFrame, toolRailClassName, toolRailThumbClassName } from '../../_components/tool-frame'
import {
  isHistoryThumbnailPending,
  sessionPagesRefetchInterval,
} from '../../_components/session-list-refresh'
import { HistoryThumbPendingOverlay } from '../../_components/tool-session-rail'
import { useRefreshSessionListOnDone } from '../../_components/use-refresh-session-list'
import { getSessionImagesVariables } from './session-images-query'

// `vectorizer` is marked deprecated in the schema in favour of `imageProcessorVectorizer`,
// but services-notifications only registered mappers for the new name on the upscaler.
// Subscribing under the new name returns invalid_argument "unhandled event".
const vectorizerSessionDoneEvent = {
  studio: {
    vectorizer: {
      sessionsCompleted: {},
    },
  },
}

const SESSION_LIST_STALE_TIME_MS = 5 * 60 * 1000

function prefetchSessionImages(queryClient: ReturnType<typeof useQueryClient>, sessionId: string) {
  const variables = getSessionImagesVariables(sessionId)
  const { pagination: _, ...otherVariables } = variables
  void queryClient.prefetchInfiniteQuery({
    // oxlint-disable-next-line oxlint-plugin-query/exhaustive-deps -- generated infinite key hashes otherVariables (request minus pagination)
    queryKey: [GetImagesQueryRootKey, JSON.stringify(otherVariables)],
    initialPageParam: undefined as string | undefined,
    queryFn: () => getImagesFn(variables),
    staleTime: SESSION_LIST_STALE_TIME_MS,
  })
}

export function SessionRail() {
  const t = useTranslations('pages.vectorizer')
  const params = useParams<{ id?: string }>()
  const activeSessionId = params?.id
  const queryClient = useQueryClient()
  useRefreshSessionListOnDone(vectorizerSessionDoneEvent, ListQueryRootKey)
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const listQuery = useListInfiniteQuery(
    { pagination: { take: 20 }, surface: Surface.MARKETPLACE },
    {
      enabled: Boolean(isLoggedIn) && process.env.NODE_ENV !== 'test',
      staleTime: SESSION_LIST_STALE_TIME_MS,
      refetchInterval: sessionPagesRefetchInterval,
    },
  )

  const sessions =
    listQuery.data?.pages.flatMap(page => page.sessions ?? []).filter(session => session.id) ?? []

  const isHome = !activeSessionId

  return (
    <nav aria-label={t('history')} className={toolRailClassName}>
      {isHome ? (
        <Button variant="primary" size="small" disabled aria-label={t('new_session')}>
          <IconAdd className="h-5 w-5" />
        </Button>
      ) : (
        <Button variant="primary" size="small" asChild>
          <Link href={AppRoutes.TOOLS_VECTORIZER} component="Link" aria-label={t('new_session')}>
            <IconAdd className="h-5 w-5" />
          </Link>
        </Button>
      )}

      {sessions.map((session, index) => {
        const href = `${AppRoutes.TOOLS_VECTORIZER}${session.id}`
        const isActive = session.id === activeSessionId
        return (
          <Link
            key={session.id}
            href={href}
            component="Link"
            aria-current={isActive ? 'page' : undefined}
            onMouseEnter={() => {
              if (session.id) {
                prefetchSessionImages(queryClient, session.id)
              }
            }}
            className={toolRailThumbClassName(isActive)}
          >
            {session.thumbnailUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={session.thumbnailUrl}
                alt=""
                width={48}
                height={48}
                decoding="async"
                loading={index < 8 ? 'eager' : 'lazy'}
                className="size-full object-cover"
              />
            ) : (
              <span className="block size-full bg-cf-grey-100" />
            )}
            {isHistoryThumbnailPending(session) ? (
              <HistoryThumbPendingOverlay label={t('processing')} />
            ) : null}
            {session.images && session.images > 1 ? (
              <span className="absolute bottom-0.5 right-0.5 rounded bg-cf-grey-1000 px-1 text-[10px] font-semibold text-cf-grey-0">
                {session.images}
              </span>
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}

export function VectorizerFrame({ children }: { children: React.ReactNode }) {
  return <ToolFrame rail={<SessionRail />}>{children}</ToolFrame>
}
